import type { Block } from './blocks/types'
import { apiFetch } from '@/auth/apiClient'

export interface DraftBody {
  blocks: Block[]
  version: number
  source_changed: boolean
  updated_at: string | null
}

export interface EnsureThreadResponse {
  thread_id: string
  draft: DraftBody
}

interface WireDraft {
  blocks: { schema_version: 1; blocks: Block[] }
  version: number
  source_changed: boolean
  updated_at: string | null
}

interface WireEnsureThreadResponse {
  thread_id: string
  draft: WireDraft
}

function unwrapDraft(wire: WireDraft): DraftBody {
  return { ...wire, blocks: wire.blocks.blocks }
}

export class VersionConflictError extends Error {
  constructor(public currentVersion: number) {
    super(`Version conflict; current=${currentVersion}`)
    this.name = 'VersionConflictError'
  }
}

const BASE = (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/proposal`

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Request failed ${res.status}: ${text || res.statusText}`)
  }
  return (await res.json()) as T
}

export async function ensureThread(
  caseId: string,
  signal?: AbortSignal,
): Promise<EnsureThreadResponse> {
  const res = await apiFetch(`${BASE(caseId)}/thread`, {
    method: 'POST',
    signal,
  })
  const wire = await jsonOrThrow<WireEnsureThreadResponse>(res)
  return { thread_id: wire.thread_id, draft: unwrapDraft(wire.draft) }
}

export async function fetchDraft(caseId: string, signal?: AbortSignal): Promise<DraftBody> {
  const res = await apiFetch(`${BASE(caseId)}/draft`, {
    method: 'GET',
    signal,
  })
  const wire = await jsonOrThrow<WireDraft>(res)
  return unwrapDraft(wire)
}

export async function patchBlock(
  caseId: string,
  block: Block,
  expectedVersion: number,
  signal?: AbortSignal,
): Promise<number> {
  const res = await apiFetch(`${BASE(caseId)}/draft/blocks/${encodeURIComponent(block.id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': String(expectedVersion) },
    body: JSON.stringify({ block }),
    signal,
  })
  if (res.status === 412) {
    const body = (await res.json().catch(() => ({}))) as { current_version?: number }
    throw new VersionConflictError(body.current_version ?? expectedVersion)
  }
  const data = await jsonOrThrow<{ version: number }>(res)
  return data.version
}

export async function deleteBlockApi(
  caseId: string,
  blockId: string,
  expectedVersion: number,
  signal?: AbortSignal,
): Promise<number> {
  const res = await apiFetch(`${BASE(caseId)}/draft/blocks/${encodeURIComponent(blockId)}`, {
    method: 'DELETE',
    headers: { 'If-Match': String(expectedVersion) },
    signal,
  })
  if (res.status === 412) {
    const body = (await res.json().catch(() => ({}))) as { current_version?: number }
    throw new VersionConflictError(body.current_version ?? expectedVersion)
  }
  const data = await jsonOrThrow<{ version: number }>(res)
  return data.version
}

export async function patchThreadId(caseId: string, threadId: string): Promise<void> {
  const res = await apiFetch(`${BASE(caseId)}/thread`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ thread_id: threadId }),
  })
  if (res.status === 204) return
  await jsonOrThrow<void>(res)
}

export async function resetDraft(caseId: string, signal?: AbortSignal): Promise<void> {
  const res = await apiFetch(`${BASE(caseId)}/draft`, {
    method: 'DELETE',
    signal,
  })
  if (res.status === 204) return
  await jsonOrThrow<void>(res)
}
